/**
 * Dynamically loads the Razorpay JS SDK and opens the checkout popup.
 * Returns a promise that resolves with the payment response on success,
 * or rejects if the user dismisses the modal or SDK fails to load.
 *
 * @param {Object} options
 * @param {string}   options.key         - Razorpay key_id (from backend)
 * @param {string}   options.orderId     - Razorpay order_id (from backend)
 * @param {number}   options.amount      - Amount in paise (e.g. 35900 for ₹359)
 * @param {string}   [options.currency]  - Default: 'INR'
 * @param {string}   [options.name]      - Business name shown on popup
 * @param {string}   [options.description]
 * @param {Object}   [options.prefill]   - { name, email, contact }
 * @param {string}   [options.color]     - Hex color for theme
 * @returns {Promise<Object>} Razorpay payment response
 */
export const openRazorpayCheckout = (options) => {
  return new Promise((resolve, reject) => {
    // Reuse existing script tag if already injected
    const existingScript = document.getElementById("razorpay-sdk");

    const init = () => {
      if (!window.Razorpay) {
        reject(new Error("Razorpay SDK failed to initialise"));
        return;
      }

      const rzpOptions = {
        key: options.key,
        amount: options.amount,
        currency: options.currency || "INR",
        name: options.name || "Seller Platform",
        description: options.description || "",
        prefill: options.prefill || {},
        theme: {
          color: options.color || "#6366f1",
        },
        handler: (response) => {
          resolve(response);
        },
        modal: {
          ondismiss: () => {
            reject(new Error("Payment cancelled by user"));
          },
        },
      };

      if (options.orderId) rzpOptions.order_id = options.orderId;
      if (options.subscription_id) rzpOptions.subscription_id = options.subscription_id;

      const rzp = new window.Razorpay(rzpOptions);

      rzp.open();
    };

    if (window.Razorpay) {
      // SDK already loaded
      init();
    } else if (existingScript) {
      // Script tag exists but not loaded yet — wait for it
      existingScript.onload = init;
      existingScript.onerror = () =>
        reject(new Error("Failed to load Razorpay SDK"));
    } else {
      // Inject script tag for the first time
      const script = document.createElement("script");
      script.id = "razorpay-sdk";
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = init;
      script.onerror = () => reject(new Error("Failed to load Razorpay SDK"));
      document.body.appendChild(script);
    }
  });
};

export default openRazorpayCheckout;